import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import type { AvisoMencao, AvisoMensagem } from '@orbind/core'
import { api } from '../../lib/api.ts'
import { isDesktop } from '../../lib/platform.ts'
import { usePresence } from '../presence/PresenceProvider.tsx'
import { canalDePresenca } from '../presence/presence-client.ts'
import { escutarDoDesktop } from '../app/desktop-eventos.ts'
import { estadoDaPermissao, notificarNoSistema, pedirPermissao } from './notificacao-nativa.ts'
import { avatarDoAutor } from './useChat.ts'
import './avisos.css'

/**
 * Avisos do chat — montado no shell, vale em qualquer tela do Orbind.
 *
 * O aviso antigo era um toast de uma linha ("Fulano: “Eai”") e o usuário achou
 * feio (2026-09-14). Agora é um CARTÃO no estilo de Discord/Slack/WhatsApp:
 * foto de quem mandou, conversa, texto em até três linhas, hora, e RESPOSTA
 * RÁPIDA sem sair da tela. Com o Orbind em segundo plano sai também a
 * notificação do Windows (ver notificacao-nativa.ts).
 *
 * Regras (mantidas):
 * - **Menção**: sempre (fura o silêncio); som só para quem está disponível.
 * - **Mensagem**: só para quem está DISPONÍVEL; o servidor nem manda para quem
 *   silenciou a conversa.
 * - Quem está olhando aquela conversa com a janela em foco não recebe nada.
 *
 * Mensagens seguidas da mesma conversa ATUALIZAM o cartão ("3 novas
 * mensagens") em vez de empilhar um por mensagem.
 */

type Cartao = {
  channelId: string
  messageId: string
  autor: string
  foto: string | null
  /** "Operações" ou "Mensagem direta". */
  onde: string
  direta: boolean
  mencao: boolean
  trecho: string
  quantas: number
  chegouEm: number
}

const MAXIMO_NA_TELA = 3
const SOME_EM_MS = 8_000

export function AvisosDoChat() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { meuEstado } = usePresence()
  const [cartoes, setCartoes] = useState<Cartao[]>([])

  const estado = useRef(meuEstado)
  estado.current = meuEstado
  const rota = useRef(location.pathname)
  rota.current = location.pathname

  const fechar = useCallback((channelId: string) => {
    setCartoes((atual) => atual.filter((c) => c.channelId !== channelId))
  }, [])

  const abrir = useCallback(
    (cartao: Pick<Cartao, 'channelId'>) => {
      fechar(cartao.channelId)
      navigate(`/channels/${cartao.channelId}`)
    },
    [fechar, navigate],
  )

  // Abrir a conversa por outro caminho some com o cartão dela.
  useEffect(() => {
    const aberta = location.pathname.match(/^\/channels\/([^/]+)/)?.[1]
    if (aberta) fechar(aberta)
  }, [location.pathname, fechar])

  /**
   * Avisos que chegaram enquanto a pessoa estava ausente ou offline.
   *
   * Ficam aqui até ela voltar — a mensagem em si já está na conversa; o que
   * espera é só o aviso. Vive numa ref, e não em estado, porque guardar não
   * muda nada na tela: só a saída deles redesenha.
   */
  const guardados = useRef<(AvisoMencao | AvisoMensagem)[]>([])

  useEffect(() => {
    return canalDePresenca.inscrever((evento) => {
      if (evento.tipo !== 'mencao' && evento.tipo !== 'mensagem') return
      const olhandoAConversa = rota.current === `/channels/${evento.channelId}` && document.hasFocus()
      if (olhandoAConversa) return

      const mencao = evento.tipo === 'mencao'
      /*
       * QUEM ESTÁ TRABALHANDO RECEBE (SPEC-20260924-0924).
       *
       * `no-computador` é a pessoa mexendo no computador com o Orbind fora de
       * vista — é justamente quem precisa do aviso do sistema. Antes desta
       * linha ela caía no mesmo balde de quem está ausente e não recebia nada.
       */
      const trabalhando = estado.current === 'disponivel' || estado.current === 'no-computador'
      /*
       * AUSENTE OU OFFLINE: o aviso fica GUARDADO, não perdido (pedido do
       * usuário, 2026-09-24). A mensagem já está na conversa — o que espera é
       * só o aviso, que sai assim que a pessoa voltar. Antes disso ele era
       * descartado, e quem voltava do almoço não sabia que tinha sido chamado.
       */
      if (!mencao && !trabalhando) {
        guardados.current.push(evento)
        return
      }
      mostrarAviso.current(evento)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Mostra um aviso: cartão na tela, som e notificação do sistema.
   *
   * Em ref porque dois caminhos chamam — o que chega agora e o que estava
   * guardado — e reassinar o canal a cada render só para trocar a função
   * derrubaria a conexão.
   */
  const mostrarAviso = useRef((evento: AvisoMencao | AvisoMensagem) => {})
  mostrarAviso.current = (evento: AvisoMencao | AvisoMensagem) => {
    {
      const mencao = evento.tipo === 'mencao'
      if (mencao) void queryClient.invalidateQueries({ queryKey: ['chat', 'channels'] })

      const novo = paraCartao(evento)
      if (estado.current === 'disponivel') apitar(mencao ? 'mencao' : 'mensagem')

      setCartoes((atual) => {
        const existente = atual.find((c) => c.channelId === novo.channelId)
        const semEle = atual.filter((c) => c.channelId !== novo.channelId)
        const combinado: Cartao = existente
          ? { ...novo, quantas: existente.quantas + 1, mencao: existente.mencao || novo.mencao }
          : novo
        // O mais novo fica em cima; acima do limite, o mais velho sai.
        return [combinado, ...semEle].slice(0, MAXIMO_NA_TELA)
      })

      notificarNoSistema({
        titulo: mencao ? `${novo.autor} mencionou você` : novo.direta ? novo.autor : `${novo.autor} · ${novo.onde}`,
        corpo: novo.trecho,
        grupo: novo.channelId,
        icone: novo.foto,
        aoClicar: () => abrir(novo),
        chat: { channelId: novo.channelId, autor: novo.autor, onde: novo.onde, direta: novo.direta },
      })
    }
  }

  /**
   * A pessoa voltou: solta o que ficou guardado (pedido 2026-09-24).
   *
   * Um cartão por conversa, como sempre — a lógica de juntar já está em
   * `mostrarAviso`, então dez mensagens do mesmo canal viram um cartão com a
   * contagem, e não dez avisos empilhados na volta do almoço.
   */
  useEffect(() => {
    const trabalhando = meuEstado === 'disponivel' || meuEstado === 'no-computador'
    if (!trabalhando || guardados.current.length === 0) return
    const pendentes = guardados.current
    guardados.current = []
    for (const evento of pendentes) mostrarAviso.current(evento)
  }, [meuEstado])

  // O TOAST DO WINDOWS (desktop, SPEC-20260916-1256) devolve por eventos do
  // shell: a resposta digitada no próprio toast vai para a conversa por aqui,
  // e o clique no toast abre a conversa (o Rust já trouxe a janela da bandeja).
  useEffect(() => {
    if (!isDesktop()) return
    const pararResposta = escutarDoDesktop<{ channelId: string; texto: string }>('orbind://chat-responder', (r) => {
      const corpo = (r.texto ?? '').trim()
      if (!r.channelId || !corpo) return
      void api.chat.channels[':id'].messages
        .$post({ param: { id: r.channelId }, json: { body: corpo } })
        .then((resposta) => {
          if (resposta.ok) {
            fechar(r.channelId)
            void queryClient.invalidateQueries({ queryKey: ['chat'] })
          }
        })
        .catch(() => undefined)
    })
    const pararAbrir = escutarDoDesktop<{ channelId: string }>('orbind://chat-abrir', (r) => {
      if (r.channelId) abrir({ channelId: r.channelId })
    })
    return () => {
      pararResposta()
      pararAbrir()
    }
  }, [abrir, fechar, queryClient])

  if (cartoes.length === 0) return null

  return createPortal(
    <div className="av-pilha" role="region" aria-label="Novas mensagens" aria-live="polite">
      {cartoes.map((cartao) => (
        <CartaoDeAviso
          key={cartao.channelId}
          cartao={cartao}
          onAbrir={() => abrir(cartao)}
          onFechar={() => fechar(cartao.channelId)}
          onRespondido={() => {
            void queryClient.invalidateQueries({ queryKey: ['chat'] })
          }}
        />
      ))}
    </div>,
    document.body,
  )
}

function CartaoDeAviso({
  cartao,
  onAbrir,
  onFechar,
  onRespondido,
}: {
  cartao: Cartao
  onAbrir: () => void
  onFechar: () => void
  onRespondido: () => void
}) {
  const [respondendo, setRespondendo] = useState(false)
  const [texto, setTexto] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [enviado, setEnviado] = useState(false)
  const [parado, setParado] = useState(false)
  const [permissao, setPermissao] = useState(estadoDaPermissao)
  const campo = useRef<HTMLInputElement>(null)

  // Some sozinho — mas NÃO enquanto o mouse está em cima, enquanto a pessoa
  // escreve a resposta, nem com a janela sem foco (voltaria e não veria nada).
  // Mensagem nova na mesma conversa (`chegouEm` muda) reinicia o prazo.
  useEffect(() => {
    if (respondendo || parado) return
    let restante = SOME_EM_MS
    let inicio = Date.now()
    let timer: ReturnType<typeof setTimeout> | null = null
    const armar = () => {
      if (!document.hasFocus()) return
      inicio = Date.now()
      timer = setTimeout(onFechar, restante)
    }
    const pausar = () => {
      if (timer) {
        clearTimeout(timer)
        timer = null
        restante = Math.max(1_500, restante - (Date.now() - inicio))
      }
    }
    armar()
    window.addEventListener('focus', armar)
    window.addEventListener('blur', pausar)
    return () => {
      if (timer) clearTimeout(timer)
      window.removeEventListener('focus', armar)
      window.removeEventListener('blur', pausar)
    }
  }, [respondendo, parado, cartao.chegouEm, onFechar])

  useEffect(() => {
    if (respondendo) campo.current?.focus()
  }, [respondendo])

  useEffect(() => {
    if (!enviado) return
    const t = setTimeout(onFechar, 1_200)
    return () => clearTimeout(t)
  }, [enviado, onFechar])

  const enviar = async () => {
    const corpo = texto.trim()
    if (!corpo || enviando) return
    setEnviando(true)
    setErro(null)
    try {
      const resposta = await api.chat.channels[':id'].messages.$post({
        param: { id: cartao.channelId },
        json: { body: corpo },
      })
      if (!resposta.ok) throw new Error()
      setEnviado(true)
      onRespondido()
    } catch {
      setErro('Não foi possível enviar. Tente de novo.')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <article
      className={`av-cartao${cartao.mencao ? ' av-cartao--mencao' : ''}`}
      onMouseEnter={() => setParado(true)}
      onMouseLeave={() => setParado(false)}
    >
      <div className="av-cartao__topo">
        <span className="av-cartao__onde">
          <i className={cartao.direta ? 'fas fa-user' : 'fas fa-users'} aria-hidden />
          {cartao.onde}
        </span>
        <span className="av-cartao__hora">agora</span>
        <button type="button" className="av-cartao__fechar" aria-label="Dispensar aviso" onClick={onFechar}>
          <i className="fas fa-times" aria-hidden />
        </button>
      </div>

      <button type="button" className="av-cartao__corpo" onClick={onAbrir} aria-label={`Abrir conversa ${cartao.onde}`}>
        <span className="av-cartao__foto" aria-hidden>
          {cartao.foto ? <img src={cartao.foto} alt="" /> : iniciais(cartao.autor)}
        </span>
        <span className="av-cartao__texto">
          <span className="av-cartao__autor">
            {cartao.autor}
            {cartao.mencao ? (
              <span className="av-cartao__selo">
                <i className="fas fa-at" aria-hidden /> mencionou você
              </span>
            ) : null}
          </span>
          <span className="av-cartao__mensagem">{cartao.trecho}</span>
          {cartao.quantas > 1 ? <span className="av-cartao__mais">{cartao.quantas} novas mensagens</span> : null}
        </span>
      </button>

      {enviado ? (
        <div className="av-cartao__enviado" role="status">
          <i className="fas fa-check" aria-hidden /> Resposta enviada
        </div>
      ) : respondendo ? (
        <form
          className="av-cartao__resposta"
          onSubmit={(e) => {
            e.preventDefault()
            void enviar()
          }}
        >
          <input
            ref={campo}
            className="av-cartao__campo"
            value={texto}
            maxLength={8000}
            placeholder={cartao.direta ? `Responder ${cartao.autor.split(' ')[0]}…` : `Responder em ${cartao.onde}…`}
            aria-label="Resposta rápida"
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setRespondendo(false)
            }}
          />
          <button
            type="submit"
            className="av-cartao__enviar"
            aria-label="Enviar resposta"
            disabled={!texto.trim() || enviando}
          >
            <i className={enviando ? 'fas fa-circle-notch fa-spin' : 'fas fa-paper-plane'} aria-hidden />
          </button>
          {erro ? <span className="av-cartao__erro">{erro}</span> : null}
        </form>
      ) : (
        <div className="av-cartao__acoes">
          <button type="button" className="av-cartao__acao" onClick={() => setRespondendo(true)}>
            <i className="fas fa-reply" aria-hidden /> Responder
          </button>
          <button type="button" className="av-cartao__acao av-cartao__acao--forte" onClick={onAbrir}>
            <i className="fas fa-comment" aria-hidden /> Abrir conversa
          </button>
        </div>
      )}

      {/* No navegador a notificação do sistema precisa de permissão; o pedido
          sai daqui, de um clique, e some depois de respondido. */}
      {!isDesktop() && permissao === 'pendente' ? (
        <button
          type="button"
          className="av-cartao__permissao"
          onClick={() => void pedirPermissao().then(setPermissao)}
        >
          <i className="fas fa-bell" aria-hidden /> Avisar também quando o Orbind estiver minimizado
        </button>
      ) : null}
    </article>
  )
}

function paraCartao(evento: AvisoMencao | AvisoMensagem): Cartao {
  const direta = evento.tipo === 'mensagem' ? evento.direta : evento.canal === 'conversa direta'
  return {
    channelId: evento.channelId,
    messageId: evento.messageId,
    autor: evento.autor,
    foto: avatarDoAutor(evento.foto),
    onde: direta ? 'Mensagem direta' : evento.canal,
    direta,
    mencao: evento.tipo === 'mencao',
    trecho: evento.trecho,
    quantas: 1,
    chegouEm: Date.now(),
  }
}

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/)
  return ((partes[0]?.[0] ?? '') + (partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? '') : '')).toUpperCase()
}

/**
 * Toques curtos, gerados na hora — sem arquivo de áudio para carregar. A
 * menção sobe (chama atenção); a mensagem comum é um toque só, mais baixo.
 * O navegador só libera áudio depois de alguma interação com a página; antes
 * disso o apito é engolido em silêncio e fica só o aviso na tela.
 */
let contexto: AudioContext | null = null
/**
 * Espaço mínimo entre dois apitos (pedido do usuário, 2026-09-16): numa rajada
 * de mensagens o som virava metralhadora. A menção tem o espaço menor — ela
 * fura o silêncio por natureza —, mas também não toca sem parar.
 */
const ESPACO_ENTRE_APITOS_MS = { mensagem: 4_000, mencao: 2_000 } as const
let ultimoApitoEm = 0

function apitar(tipo: 'mencao' | 'mensagem') {
  const agora = Date.now()
  if (agora - ultimoApitoEm < ESPACO_ENTRE_APITOS_MS[tipo]) return
  ultimoApitoEm = agora
  try {
    contexto ??= new AudioContext()
    const agora = contexto.currentTime
    const notas: readonly (readonly [number, number])[] =
      tipo === 'mencao'
        ? [
            [0, 880],
            [0.16, 1320],
          ]
        : [[0, 988]]
    const pico = tipo === 'mencao' ? 0.25 : 0.12
    for (const [atraso, frequencia] of notas) {
      const osc = contexto.createOscillator()
      const volume = contexto.createGain()
      osc.type = 'sine'
      osc.frequency.value = frequencia
      volume.gain.setValueAtTime(0.0001, agora + atraso)
      volume.gain.exponentialRampToValueAtTime(pico, agora + atraso + 0.02)
      volume.gain.exponentialRampToValueAtTime(0.0001, agora + atraso + 0.14)
      osc.connect(volume).connect(contexto.destination)
      osc.start(agora + atraso)
      osc.stop(agora + atraso + 0.15)
    }
  } catch {
    // Sem áudio disponível: fica o aviso visual.
  }
}
